import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

const BookingContext = createContext(null);

export const STEPS = ["category", "service", "date", "time", "details", "review", "success"];

const RESUME_KEY = "pout_booking_resume";

/** Write a resume payload so the next `/` visit pre-fills service + customer and jumps to `date`. */
export function stashBookingResume({ serviceId, customer }) {
    try {
        sessionStorage.setItem(RESUME_KEY, JSON.stringify({
            serviceId: serviceId ? Number(serviceId) : null,
            customer: customer || null,
        }));
    } catch { /* private mode */ }
}

function readResume() {
    try {
        const raw = sessionStorage.getItem(RESUME_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(RESUME_KEY);
        return JSON.parse(raw);
    } catch { return null; }
}

export function BookingProvider({ children }) {
    const [step, setStep] = useState("category");
    const [direction, setDirection] = useState(1);
    const [category, setCategory] = useState(null);
    const [service, setService] = useState(null);
    const [cart, setCartState] = useState([]);
    const [date, setDate] = useState(null);
    const [time, setTime] = useState(null);
    const setCart = useCallback((items) => { setCartState(items); setDate(null); setTime(null); }, []);
    const [customer, setCustomer] = useState({
        firstName: "", lastName: "", email: "", phone: "", notes: "",
    });
    const [confirmation, setConfirmation] = useState(null);
    const [resumedFrom, setResumedFrom] = useState(null);

    // On mount: if the previous page stashed a resume payload, hydrate it.
    useEffect(() => {
        const r = readResume();
        if (!r || !r.serviceId) return;
        // Pre-fill customer immediately so the details step is a no-op.
        if (r.customer) {
            setCustomer((prev) => ({ ...prev, ...r.customer, notes: prev.notes || (r.customer.notes || "") }));
        }
        // Look up the FULL service record by id. Amelia's `/booking/services`
        // returns every service when called without a category filter.
        api.listServices().then((data) => {
            const items = Array.isArray(data) ? data : (data?.services || []);
            const svc = items.find((s) => Number(s.id) === Number(r.serviceId));
            if (svc) {
                setService(svc);
                setCategory(svc.category || svc.categoryName || null);
                setDirection(1);
                setStep("date");
                setResumedFrom("reschedule");
            }
        }).catch(() => { /* silently keep the empty flow */ });
    }, []);

    const goTo = useCallback((next) => {
        const from = STEPS.indexOf(step);
        const to = STEPS.indexOf(next);
        setDirection(to >= from ? 1 : -1);
        setStep(next);
    }, [step]);

    const next = useCallback(() => {
        const i = STEPS.indexOf(step);
        if (i < STEPS.length - 1) goTo(STEPS[i + 1]);
    }, [step, goTo]);

    const back = useCallback(() => {
        const i = STEPS.indexOf(step);
        if (i > 0) goTo(STEPS[i - 1]);
    }, [step, goTo]);

    const reset = useCallback(() => {
        setDirection(-1);
        setStep("category");
        setCategory(null);
        setService(null);
        setCartState([]);
        setDate(null);
        setTime(null);
        setCustomer({ firstName: "", lastName: "", email: "", phone: "", notes: "" });
        setConfirmation(null);
    }, []);

    const value = useMemo(() => ({
        step, direction, goTo, next, back, reset,
        category, setCategory,
        service, setService,
        cart, setCart,
        date, setDate,
        time, setTime,
        customer, setCustomer,
        confirmation, setConfirmation,
        resumedFrom,
    }), [step, direction, goTo, next, back, reset, category, service, cart, setCart, date, time, customer, confirmation, resumedFrom]);

    return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
    const ctx = useContext(BookingContext);
    if (!ctx) throw new Error("useBooking must be used inside BookingProvider");
    return ctx;
}
